/**
 * Reader for the advisory `acs_user_status` cookie, written by AEM on login
 * (SsoProcessLogin / UserStatusCookieUtil). Unsigned and browser-editable —
 * never auth, entitlement, or access control. Unusable cookies become the
 * anonymous user; nothing here throws to consumers.
 *
 * This is a deliberate parallel implementation of the ACS.org module at
 * wso-aem-reactor aem-6/ui.cdn/js/redesign/user-status-cookie.js, which cannot
 * be imported here. Keep the two behaviourally in step.
 */
import { debugit } from './acs-debug.js';

export const COOKIE_NAME = 'acs_user_status';
const SUPPORTED_VERSION = 1;
const COMMUNITY_MEMBER_TYPES = ['COMMUNITY_ASSOCIATE', 'COMMUNITY_MEMBER'];
const PACKAGE_CODES = ['BASIC', 'STANDARD', 'PREMIUM'];

/** Diagnostics only. Consumers should treat anything other than `ok` as anonymous. */
export const PARSE_STATUS = {
  OK: 'ok',
  MISSING: 'missing',
  MALFORMED_B64: 'malformed_b64',
  MALFORMED_JSON: 'malformed_json',
  INVALID_PAYLOAD: 'invalid_payload',
  UNSUPPORTED_VERSION: 'unsupported_version',
  EXPIRED: 'expired',
  NOT_LOGGED_IN: 'not_logged_in',
};

const asString = value => (typeof value === 'string' ? value : '');

function result(status, payload = null) {
  if (status !== PARSE_STATUS.OK) {
    debugit('acs_user_status: unusable cookie, treating as anonymous:', status);
  }
  return { status, payload };
}

function readRawCookie(cookieString, name) {
  if (typeof cookieString !== 'string') {
    return '';
  }

  for (const part of cookieString.split(';')) {
    const eq = part.indexOf('=');
    if (eq >= 0 && part.slice(0, eq).trim() === name) {
      return part.slice(eq + 1).trim();
    }
  }
  return '';
}

/** Mirror of Java `Base64.getUrlEncoder().withoutPadding()`. Throws if not decodable. */
function decodeBase64Url(value) {
  const padded = value.replace(/-/g, '+').replace(/_/g, '/');
  if (padded.length % 4 === 1) {
    throw new Error('invalid base64url length');
  }

  const binary = atob(padded + '='.repeat((4 - (padded.length % 4)) % 4));
  return decodeURIComponent([...binary].map(ch => `%${ch.charCodeAt(0).toString(16).padStart(2, '0')}`).join(''));
}

export function parseUserStatusValue(value) {
  if (!value) {
    return result(PARSE_STATUS.MISSING);
  }

  let json;
  try {
    json = decodeBase64Url(value);
  } catch {
    return result(PARSE_STATUS.MALFORMED_B64);
  }

  let payload;
  try {
    payload = JSON.parse(json);
  } catch {
    return result(PARSE_STATUS.MALFORMED_JSON);
  }

  if (payload === null || typeof payload !== 'object' || Array.isArray(payload)) {
    return result(PARSE_STATUS.INVALID_PAYLOAD);
  }
  if (payload.v !== SUPPORTED_VERSION) {
    return result(PARSE_STATUS.UNSUPPORTED_VERSION);
  }
  if (typeof payload.exp !== 'number' || !Number.isFinite(payload.exp)) {
    return result(PARSE_STATUS.INVALID_PAYLOAD);
  }
  if (Date.now() >= payload.exp) {
    return result(PARSE_STATUS.EXPIRED);
  }
  if (payload.loggedin !== true) {
    return result(PARSE_STATUS.NOT_LOGGED_IN);
  }

  return result(PARSE_STATUS.OK, payload);
}

export function readUserStatus(cookieString = typeof document !== 'undefined' ? document.cookie : '') {
  return parseUserStatusValue(readRawCookie(cookieString, COOKIE_NAME));
}

export function anonymousUser() {
  return { loggedin: false, haspackage: false, communitymember: false };
}

/** Legacy `acs.user` shape. `fullname` aliases `firstname`; the cookie carries no last name. */
export function toCompatibilityUser(parseResult) {
  if (parseResult?.status !== PARSE_STATUS.OK) {
    return anonymousUser();
  }

  const { firstname, packagecode, membertype, ismember, ispaid, hasrenewal } = parseResult.payload;
  const name = asString(firstname);
  const code = asString(packagecode);
  const type = asString(membertype);

  return {
    loggedin: true,
    firstname: name,
    ...(name && { fullname: name }),
    ismember: ismember === true,
    ispaid: ispaid === true,
    hasrenewal: hasrenewal === true,
    packagecode: code,
    membertype: type,
    communitymember: COMMUNITY_MEMBER_TYPES.includes(type),
    haspackage: PACKAGE_CODES.includes(code),
  };
}
